
























































































































































































































import { Component, Prop, Mixins } from "vue-property-decorator";
import _ from "@/boot/lodash";

// Mixins
import HasValidation from "@/mixins/HasValidation";
import Context from "@/mixins/context";

// Enums
import { DataModules } from "@/store/modules/data";

// Types
import { IClient, IChildAccount, IChildAccountForm } from "@/models/clients";
import { AdminRoutes } from "@/data/enums/router";

@Component({
  components: {
    "client-list-item": () =>
      import("@/components/app/admin/clients/clientListItem.vue")
  }
})
export default class AddEditChildAccountModal extends Mixins(
  HasValidation,
  Context
) {
  // Props

  @Prop({ required: true })
  public parentClient!: IClient;

  @Prop({ required: true })
  public childClient!: IClient;

  @Prop({ default: null })
  public childAccount!: IChildAccount;

  @Prop({ default: false })
  public isParent!: boolean;

  // Data

  public form = {} as IChildAccountForm;
  public isProcessing = false;

  // Life-cycle method

  public created() {
    this.initialiseForm();
  }

  // Methods

  public initialiseForm() {
    this.form = this.initiateForm();
  }

  public initiateForm() {
    if (!this.isCreate)
      return _.pick(
        this.childAccount,
        _.compact([
          this.showAllowImpersonation ? "allow_impersonation" : null,
          this.showInheritPaymentDetails ? "inherit_payment_details" : null
        ])
      );

    return {
      ...(this.showAllowImpersonation ? { allow_impersonation: true } : {}),
      ...(this.showInheritPaymentDetails
        ? { inherit_payment_details: true }
        : {}),
      child_client_id: this.childClient.id
    };
  }

  public async validateBeforeSubmit() {
    try {
      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");

      this.isProcessing = true;

      this.isCreate
        ? await this.createChildAccount()
        : await this.updateChildRelation();

      this.$store.dispatch("toast/show", {
        message: this.isCreate
          ? this.$t(
              "_sentence.related_accounts.child_account_successfully_attached"
            )
          : this.$t(
              "_sentence.related_accounts.child_account_successfully_updated"
            )
      });

      this.$emit("success");
      this.$emit("close");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public createChildAccount() {
    return this.$store.dispatch(
      `data/${DataModules.CLIENTS_CHILD_ACCOUNTS}/create`,
      {
        id: this.parentClient?.id,
        data: this.form
      }
    );
  }

  public updateChildRelation() {
    return this.$store.dispatch(
      `data/${DataModules.CLIENTS_CHILD_ACCOUNTS}/update`,
      {
        id: this.parentClient?.id,
        configId: this.childAccount.id,
        data: _.omitBy(this.form, (v, k) => {
          return this.initiateForm()[k] === v;
        })
      }
    );
  }

  public detachRelation() {
    if (!this.childAccount || !this.isAdmin) return Promise.resolve();
    return this.$store.dispatch(
      `data/${DataModules.CLIENTS_CHILD_ACCOUNTS}/detachChildAccount`,
      {
        parentId: this.parentClient?.id,
        configId: this.childAccount?.id,
        onSuccess: () => {
          this.$emit("close");
          this.$emit("success");
          this.routeToParent();
          this.$store.dispatch("toast/show", {
            message: this.$t(
              "_sentence.related_accounts.child_account_successfully_detached"
            )
          });
        }
      }
    );
  }

  public selectNewClient() {
    if (!this.isAdmin) return;
    if (!this.isCreate) return;
    this.$emit("selectNewClient");
    this.$emit("close");
  }

  public routeToParent() {
    return this.$router
      .push({
        name: AdminRoutes.CLIENT_ACCOUNTS_CHILD_ACCOUNTS,
        params: {
          clientId: this.parentClient?.id
        }
      })
      .catch(err => err);
  }

  public impersonateChild() {
    if (this.canImpersonate)
      return this.$store.dispatch(
        "auth/client/impersonateChild",
        this.childAccount
      );
  }

  // Computed

  public get canImpersonate() {
    return this.childAccount?.allow_impersonation;
  }

  public get isCreate() {
    return !this.childAccount;
  }

  public get parentName() {
    return this.parentClient?.firstname || this.parentClient?.public_name;
  }

  public get childName() {
    return this.childClient?.firstname || this.childClient?.public_name;
  }

  public get childEmail() {
    return this.childClient?.default_email?.email;
  }

  public get title() {
    return this.$t(
      this.isAdmin
        ? "_action.manage_parent_child_relations"
        : this.isParent
        ? "_sentence.related_accounts.manage_your_relation_with_child"
        : "_sentence.related_accounts.manage_your_relation_with_parent",
      { childName: this.childName, parentName: this.parentName }
    );
  }

  public get formIsDisabled() {
    return this.isProcessing || !this.formIsChanged || this.hasErrors;
  }

  public get formIsChanged() {
    if (this.isCreate) return true;
    return !this.$_.isEqual(this.form, this.initiateForm());
  }

  public get showAllowImpersonation() {
    if (this.isAdmin) return true;
    return !this.isParent;
  }

  public get allowImpersonationDescPath() {
    return this.isAdmin
      ? "_sentence.related_accounts.allow_impersonation_staff_desc"
      : "_sentence.related_accounts.allow_impersonation_child_desc";
  }

  public get showInheritPaymentDetails() {
    if (this.isAdmin) return true;
    return this.isParent;
  }

  public get inheritPaymentDetailsDescPath() {
    return this.isAdmin
      ? "_sentence.related_accounts.inherit_payment_details_staff_desc"
      : "_sentence.related_accounts.inherit_payment_details_parent_desc";
  }

  public get childItemBindings() {
    return {
      action: this.isAdmin
        ? this.isCreate
          ? (this.$t("_action.change_client") as string)
          : (this.$t("_action.view_client") as string)
        : this.canImpersonate
        ? (this.$t("_action.login_as_child") as string)
        : (this.$t("_.login_disabled_by_child") as string),
      actionIsDisabled: this.isParent && !this.canImpersonate,
      client: this.childClient,
      isClickable: this.isAdmin && !this.isCreate
    };
  }

  public get childItemListeners() {
    if (this.isAdmin && this.isCreate)
      return { ctaClick: this.selectNewClient };
    else if (this.isParent) return { ctaClick: this.impersonateChild };
    else return {};
  }

  public get childAvatarBindings() {
    return {
      name: this.childClient.fullname,
      src: this.childClient.image_url,
      size: this.$store.getters["ui/isTabletPlus"]
        ? 64
        : this.$store.getters["ui/isMobile"]
        ? 40
        : 48
    };
  }

  public get parentItemBindings() {
    return {
      action: this.isAdmin ? (this.$t("_action.view_client") as string) : "",
      client: this.parentClient,
      isClickable: this.isAdmin,
      defaultRoute: () => ({
        name: AdminRoutes.CLIENT_ACCOUNTS_CHILD_ACCOUNTS,
        params: {
          clientId: this.parentClient?.id
        }
      })
    };
  }

  public get parentAvatarBindings() {
    return {
      name: this.parentClient.fullname,
      src: this.parentClient.image_url,
      size: this.$store.getters["ui/isTabletPlus"]
        ? 64
        : this.$store.getters["ui/isMobile"]
        ? 40
        : 48
    };
  }
}
